import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { ProductCard } from "@/components/ui/ProductCard";
import { CarouselTrack } from "@/components/ui/sections/_lib/CarouselTrack";
import { Typography } from "@/components/ui/Typography";
import type { ProductsRoute } from "@/data/routes/products.route";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";
import { Reveal } from "../Reveal";

const DESKTOP_PRODUCTS_PER_VIEW = 4;

export interface ExplorationProductProps extends ComponentProps<"section"> {
	heading?: string;
	linkLabel?: string;
	linkHref?: string;
	products?: ProductsRoute["exploration"]["products"];
	bg?: string;
}

export const ExplorationProduct = ({
	heading,
	linkLabel,
	linkHref,
	products,
	className,
	bg,
	...rest
}: ExplorationProductProps) => {
	const items = products ?? [];
	const desktopAlwaysCarousel = items.length > DESKTOP_PRODUCTS_PER_VIEW;
	const layout = desktopAlwaysCarousel ? "quartets" : "fill";

	const hasHeading = Boolean(heading);
	const hasLink = Boolean(linkLabel && linkHref);

	const darkMode = isDarkMode(bg);

	return (
		<section {...rest} style={{ backgroundColor: bg ? bg : undefined }}>
			<Container size="large">
				<div
					className={clsx(
						"py-grid-section-vertical-lg flex flex-col gap-grid-section-vertical-md",
						className,
					)}
				>
					{(hasHeading || hasLink) && (
						<div className="flex w-full items-end gap-grid-component-spacing-2xs">
							<div className="flex min-w-px flex-1 flex-col gap-grid-component-spacing-xs">
								{heading && (
									<Typography
										as="h2"
										variant="heading-display-xs"
										className={clsx(
											"w-full text-text-dark-text-1",
											darkMode && "text-text-light-text-1",
										)}
									>
										{heading}
									</Typography>
								)}
							</div>
							{hasLink && linkHref && (
								<Link
									as={NextLink}
									href={linkHref}
									variant="primary"
									size="medium"
									className={clsx(darkMode && "text-text-light-text-1")}
								>
									{linkLabel}
								</Link>
							)}
						</div>
					)}
					<CarouselTrack layout={layout} desktopAlwaysCarousel={desktopAlwaysCarousel}>
						{items.map((product, i) => (
							<Reveal key={product.id} animation="fade-bottom" delay={100 * (i + 1)}>
								<ProductCard key={product.id} product={product} size="large" darkMode={darkMode} />
							</Reveal>
						))}
					</CarouselTrack>
				</div>
			</Container>
		</section>
	);
};
